'use client'

import type { DesktopLinkProps } from '@repo/studio-routes-desktop-marketing-pages/desktop-link'
import { DesktopRoutingProvider } from '@repo/studio-routes-desktop-marketing-pages/desktop-link'
import {
  DESKTOP_STUDIO_BASE_PATH,
  desktopHrefToString,
} from '@repo/studio-routes-desktop-marketing-pages/desktop-routing'
import { scrollDesktopHashIntoView } from '@repo/studio-routes-desktop-marketing-pages/scroll-desktop-hash'
import Link from 'next/link'
import * as React from 'react'
import { usePathname } from 'next/navigation'

function NextDesktopLink({ href, onClick, ...props }: DesktopLinkProps) {
  return (
    <Link
      href={href}
      onClick={event => {
        onClick?.(event)
        if (!desktopHrefToString(href).includes('#')) {
          return
        }
        requestAnimationFrame(() => {
          window.dispatchEvent(new Event('hashchange'))
        })
      }}
      {...props}
    />
  )
}

function NextDesktopHashScroll() {
  const pathname = usePathname()
  const [hash, setHash] = React.useState('')

  React.useLayoutEffect(() => {
    const syncHash = () => setHash(window.location.hash)
    syncHash()
    window.addEventListener('hashchange', syncHash)
    window.addEventListener('popstate', syncHash)
    return () => {
      window.removeEventListener('hashchange', syncHash)
      window.removeEventListener('popstate', syncHash)
    }
  }, [pathname])

  React.useLayoutEffect(() => {
    if (!hash) {
      return
    }

    if (scrollDesktopHashIntoView(hash)) {
      return
    }

    const observer = new MutationObserver(() => {
      if (scrollDesktopHashIntoView(hash)) {
        observer.disconnect()
      }
    })

    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [pathname, hash])

  return null
}

export function NextDesktopRouting({ children }: { children: React.ReactNode }) {
  return (
    <DesktopRoutingProvider basePath={DESKTOP_STUDIO_BASE_PATH} Link={NextDesktopLink}>
      <NextDesktopHashScroll />
      {children}
    </DesktopRoutingProvider>
  )
}
